import React, { useMemo } from 'react';
import { Checkbox, Row, Typography } from 'antd';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import HoverHelp from '../HoverHelp';
import { safeExtractFromObject } from '../../helpers/helpers';
import Colors from '../../constants/Colors';

const { Text } = Typography;

export default function OnTraccrCheckbox({
  label,
  value,
  onChange,
  disabled,
  hoverNode,
  style,
  textStyle,
  checkboxStyle,
  hoverIconProps,
  defaultChecked = false,
  onMouseEnter,
  onMouseLeave,
  helpPlacement = 'topRight',
  indeterminate = false,
  onClick,
  checkboxClasses = [],
}) {
  const settings = useSelector((state) => safeExtractFromObject(state, 'settings.company.settings'));
  const handleCheckboxChange = (e) => {
    onChange(e?.target?.checked);
  };

  const formattedTextStyle = settings?.uxRedesignPt1
    ? {
      fontSize: 14,
      lineHeight: '18px',
      padding: 0,
      color: Colors.REDESIGN_PRIMARY_TEXT,
      fontWeight: '500',
      fontFamily: 'open-sans',
      ...textStyle,
    }
    : {
      color: 'black',
      ...textStyle,
    };

  // We need to create the checkbox like this
  // Otherwise if there is no label, the checkbox will have additional whitespace
  const checkbox = useMemo(() => {
    const props = {
      style: { marginRight: 10, ...checkboxStyle },
      checked: value,
      onChange: handleCheckboxChange,
      disabled,
      className: settings?.uxRedesignPt1 ? ['ontraccr-checkbox', ...checkboxClasses].join(' ') : '',
      defaultChecked,
      onMouseEnter,
      onMouseLeave,
      indeterminate,
      onClick,
    };

    if (label) {
      return (
        // eslint-disable-next-line react/jsx-props-no-spreading
        <Checkbox {...props}>
          <Text style={formattedTextStyle}>{label}</Text>
        </Checkbox>
      );
    }

    return (
      // eslint-disable-next-line react/jsx-props-no-spreading
      <Checkbox {...props} />
    );
  }, [
    value,
    handleCheckboxChange,
    checkboxStyle,
    disabled,
    settings,
    defaultChecked,
    onMouseEnter,
    onMouseLeave,
    label,
    formattedTextStyle,
    indeterminate,
    onClick,
    checkboxClasses,
  ]);

  return (
    <Row style={style}>
      {checkbox}
      {!!hoverNode && (
        <HoverHelp
          iconProps={{ style: { marginLeft: -10, marginTop: 5 }, ...hoverIconProps }}
          placement={helpPlacement}
          content={hoverNode}
        />
      )}
    </Row>
  );
}

OnTraccrCheckbox.propTypes = {
  label: PropTypes.node,
  value: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  hoverNode: PropTypes.node,
  style: PropTypes.shape({}),
  textStyle: PropTypes.shape({}),
  checkboxStyle: PropTypes.shape({}),
  hoverIconProps: PropTypes.shape({}),
  defaultChecked: PropTypes.bool,
  onMouseEnter: PropTypes.func,
  onMouseLeave: PropTypes.func,
  helpPlacement: PropTypes.string,
  indeterminate: PropTypes.bool,
  onClick: PropTypes.func,
  checkboxClasses: PropTypes.arrayOf(PropTypes.string),
};

OnTraccrCheckbox.defaultProps = {
  disabled: false,
  hoverNode: null,
  style: {},
  textStyle: {},
  checkboxStyle: {},
  hoverIconProps: {},
  defaultChecked: false,
  onMouseEnter: null,
  onMouseLeave: null,
  helpPlacement: 'topRight',
  label: null,
  indeterminate: false,
  onClick: null,
  checkboxClasses: [],
};
